import React, { useState, useEffect } from 'react';
import { Shield, MessageSquare, AlertTriangle, Eye, EyeOff, Award, Ban, CheckCircle } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const ChatManagement = () => {
  const [messages, setMessages] = useState([]);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('messages');

  useEffect(() => {
    fetchData();
  }, [filter, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'messages') {
        const { data } = await api.get(`/chat/admin/messages?filter=${filter}`);
        setMessages(data.data);
      } else if (activeTab === 'reports') {
        const { data } = await api.get('/chat/admin/reports');
        setReports(data.data);
      }
      
      const statsRes = await api.get('/chat/admin/stats');
      setStats(statsRes.data.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockMessage = async (messageId, reason) => {
    if (!reason) reason = prompt('Enter reason for blocking:');
    if (!reason) return;

    try {
      await api.post('/chat/admin/block-message', { messageId, reason });
      toast.success('Message blocked');
      fetchData();
    } catch (error) {
      toast.error('Failed to block message');
    }
  };

  const handleUnblockMessage = async (messageId) => {
    try {
      await api.post(`/chat/admin/unblock-message/${messageId}`);
      toast.success('Message unblocked');
      fetchData();
    } catch (error) {
      toast.error('Failed to unblock message');
    }
  };

  const handleBlockUser = async (userId, username) => {
    const duration = prompt(`Block ${username} from chat? Enter days (0 for permanent):`);
    if (duration === null) return;
    
    const reason = prompt('Enter reason:');
    if (!reason) return;

    try {
      await api.post('/chat/admin/block-user', { 
        userId, 
        duration: parseInt(duration), 
        reason 
      });
      toast.success('User blocked from chat');
      fetchData();
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const handleVerifyUser = async (userId, isVerified) => {
    try {
      if (isVerified) {
        await api.post(`/chat/admin/unverify-user/${userId}`);
        toast.success('User unverified');
      } else {
        await api.post(`/chat/admin/verify-user/${userId}`);
        toast.success('User verified');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to update verification');
    }
  };

  const handleSetBadge = async (userId, username) => {
    const badge = prompt(
      `Set badge for ${username}:\n` +
      'Options: none, verified, master, top_rated, vip, moderator'
    );
    
    if (!badge) return;
    
    const validBadges = ['none', 'verified', 'master', 'top_rated', 'vip', 'moderator'];
    if (!validBadges.includes(badge)) {
      toast.error('Invalid badge');
      return;
    }

    try {
      await api.post(`/chat/admin/set-badge/${userId}`, { badge });
      toast.success('Badge updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update badge');
    }
  };

  const getBadgeDisplay = (badge) => {
    const badges = {
      verified: { icon: '✓', color: '#00D9FF', label: 'Verified' },
      master: { icon: '👑', color: '#FFD700', label: 'Master' },
      top_rated: { icon: '⭐', color: '#FFA502', label: 'Top Rated' },
      vip: { icon: '💎', color: '#9B59B6', label: 'VIP' },
      moderator: { icon: '🛡️', color: '#E74C3C', label: 'Moderator' }
    };
    
    return badges[badge] || null;
  };

  if (loading) {
    return <div className="text-center py-20">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">Chat Management</h1>
          <p className="text-gray-400">Monitor and moderate global chat</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Messages</p>
              <p className="text-2xl font-bold">{stats.totalMessages || 0}</p>
            </div>
            <MessageSquare className="text-primary" size={32} />
          </div>
        </div>
        
        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Blocked</p>
              <p className="text-2xl font-bold text-red-500">{stats.blockedMessages || 0}</p>
            </div>
            <Ban className="text-red-500" size={32} />
          </div>
        </div>

        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Reported</p>
              <p className="text-2xl font-bold text-yellow-500">{stats.reportedMessages || 0}</p>
            </div>
            <AlertTriangle className="text-yellow-500" size={32} />
          </div>
        </div>

        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Active Users</p>
              <p className="text-2xl font-bold text-green-500">{stats.activeUsers || 0}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>

        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Blocked Users</p>
              <p className="text-2xl font-bold text-orange-500">{stats.blockedUsers || 0}</p>
            </div>
            <Shield className="text-orange-500" size={32} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-800">
        <button
          onClick={() => setActiveTab('messages')}
          className={`px-4 py-2 font-semibold ${
            activeTab === 'messages'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-400'
          }`}
        >
          Messages
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`px-4 py-2 font-semibold ${
            activeTab === 'reports'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-400'
          }`}
        >
          Reports ({reports.length})
        </button>
      </div>

      {/* Filters */}
      {activeTab === 'messages' && (
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg ${
              filter === 'all' ? 'bg-primary text-white' : 'bg-gray-800 text-gray-400'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('reported')}
            className={`px-4 py-2 rounded-lg ${
              filter === 'reported' ? 'bg-yellow-600 text-white' : 'bg-gray-800 text-gray-400'
            }`}
          >
            Reported
          </button>
          <button
            onClick={() => setFilter('blocked')}
            className={`px-4 py-2 rounded-lg ${
              filter === 'blocked' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400'
            }`}
          >
            Blocked
          </button>
        </div>
      )}

      {/* Messages List */}
      {activeTab === 'messages' && (
        <div className="gradient-card border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark border-b border-gray-800">
                <tr>
                  <th className="px-6 py-4 text-left">User</th>
                  <th className="px-6 py-4 text-left">Message</th>
                  <th className="px-6 py-4 text-left">Time</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((msg) => (
                  <tr key={msg._id} className="border-b border-gray-800 hover:bg-dark/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                          style={{ backgroundColor: msg.user?.chatColor || '#6C63FF' }}
                        >
                          {msg.user?.username?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{msg.user?.username}</span>
                            {msg.user?.isVerified && <span className="text-blue-400">✓</span>}
                            {getBadgeDisplay(msg.user?.badge) && (
                              <span 
                                className="text-xs px-2 py-1 rounded-full"
                                style={{ backgroundColor: getBadgeDisplay(msg.user?.badge).color + '20', color: getBadgeDisplay(msg.user?.badge).color }}
                              >
                                {getBadgeDisplay(msg.user?.badge).icon} {getBadgeDisplay(msg.user?.badge).label}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-400">Level {msg.user?.level || 1}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={msg.isBlocked ? 'line-through text-gray-600' : ''}>
                        {msg.message}
                      </div>
                      {msg.reportCount > 0 && (
                        <span className="text-xs text-yellow-500">⚠️ {msg.reportCount} reports</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(msg.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      {msg.isBlocked ? (
                        <span className="px-3 py-1 bg-red-500/20 text-red-500 rounded-full text-xs">Blocked</span>
                      ) : msg.isReported ? (
                        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-xs">Reported</span>
                      ) : (
                        <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-xs">Active</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {!msg.isBlocked ? (
                          <button
                            onClick={() => handleBlockMessage(msg._id)}
                            className="p-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                            title="Block Message"
                          >
                            <EyeOff size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnblockMessage(msg._id)}
                            className="p-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30"
                            title="Unblock Message"
                          >
                            <Eye size={16} />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleBlockUser(msg.user?._id, msg.user?.username)}
                          className="p-2 bg-orange-500/20 text-orange-500 rounded-lg hover:bg-orange-500/30"
                          title="Block User"
                        >
                          <Ban size={16} />
                        </button>

                        <button
                          onClick={() => handleVerifyUser(msg.user?._id, msg.user?.isVerified)}
                          className={`p-2 rounded-lg ${
                            msg.user?.isVerified 
                              ? 'bg-gray-500/20 text-gray-500 hover:bg-gray-500/30' 
                              : 'bg-blue-500/20 text-blue-500 hover:bg-blue-500/30'
                          }`}
                          title={msg.user?.isVerified ? 'Unverify User' : 'Verify User'}
                        >
                          <CheckCircle size={16} />
                        </button>

                        <button
                          onClick={() => handleSetBadge(msg.user?._id, msg.user?.username)}
                          className="p-2 bg-purple-500/20 text-purple-500 rounded-lg hover:bg-purple-500/30"
                          title="Set Badge"
                        >
                          <Award size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reports List */}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          {reports.map((report) => (
            <div key={report._id} className="gradient-card border border-gray-800 rounded-xl p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-xs font-semibold">
                      {report.reason.toUpperCase()}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      report.status === 'pending' ? 'bg-orange-500/20 text-orange-500' :
                      report.status === 'reviewed' ? 'bg-blue-500/20 text-blue-500' :
                      'bg-green-500/20 text-green-500'
                    }`}>
                      {report.status}
                    </span>
                  </div>
                  
                  <p className="text-gray-300 mb-2">
                    <strong>Message:</strong> {report.message?.message}
                  </p>
                  
                  {report.description && (
                    <p className="text-gray-400 text-sm mb-2">
                      <strong>Reason:</strong> {report.description}
                    </p>
                  )}
                  
                  <p className="text-gray-500 text-xs">
                    Reported by: {report.reportedBy?.username} • {new Date(report.createdAt).toLocaleString()}
                  </p>
                </div>

                <div className="flex gap-2">
                  {report.status === 'pending' && (
                    <>
                      <button
                        onClick={async () => {
                          await api.put(`/chat/admin/reports/${report._id}`, { status: 'reviewed' });
                          fetchData();
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                      >
                        Mark Reviewed
                      </button>
                      <button
                        onClick={async () => {
                          await api.put(`/chat/admin/reports/${report._id}`, { status: 'resolved' });
                          fetchData();
                        }}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                      >
                        Resolve
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default ChatManagement;